import React, { useState } from 'react';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import ReactPlayer from 'react-player';
import { CircleSpinner } from 'react-spinners-kit';
import styled from 'styled-components';
import { videos } from '../../../assets/videos';
import { getMuxHlsPlayback } from '../../../util/mux-utilities';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhFormGridColumn } from '../../core/RhFormGrid';
import { RhRawButton } from '../../core/RhRawButton';
import { SizedBox } from '../../core/SizedBox';
import { colors, H1, H2, H3 } from '../../core/Styles';
import { StreamOnThirdPartyProps } from '../containers/StreamOnThirdPartyContainer';
import { BiHide, BiShow } from 'react-icons/bi';

export const StreamOnThirdPartyScreen: React.FC<StreamOnThirdPartyProps> = props => {
  // Ephemeral state
  const [isTutorialHidden, setIsTutorialHidden] = useState(!!props.event?.muxPlaybackID);

  return (
    <DashboardWrapper>
      <DashboardHeader
        title={<H1>{props.event?.title}</H1>}
        actions={[
          props.isLoadingEvent && <CircleSpinner key="spinner" size={15} color={colors.primary} />,
          props.event?.muxID ? (
            <RhButton
              key="end-button"
              title="End Event"
              disabled={props.isEndingStream || props.hasEventPassed}
              loading={props.isEndingStream}
              onClick={props.userClickedEndEvent}
            />
          ) : (
            <RhButton
              key="start-button"
              title="Start Event"
              disabled={props.isLoadingEvent}
              loading={props.isLoadingEvent}
              onClick={props.userClickedStartEvent}
            />
          ),
        ]}
      />
      <StreamViewGrid>
        <RhFormGridColumn>
          {props.event?.muxPlaybackID && (
            <PlayerDiv>
              <PlayerFloater>
                <H2>Stream Preview</H2>
              </PlayerFloater>
              <ReactPlayer
                url={getMuxHlsPlayback(props.event.muxPlaybackID)}
                controls={true}
                muted={true}
                playing={true}
                width="100%"
                height="100%"
              />
            </PlayerDiv>
          )}
          <div>
            <HorizontalContent
              left={<H3>Starting the stream</H3>}
              right={
                <RhButton
                  mode={RhButtonMode.text}
                  suffixIcon={isTutorialHidden ? BiShow : BiHide}
                  title={`${isTutorialHidden ? 'Show' : 'Hide'} this section`}
                  onClick={() => setIsTutorialHidden(prev => !prev)}
                />
              }
            />
            <SizedBox height={16} />
            {!isTutorialHidden && (
              <>
                <div>
                  To start streaming this event, you need to install a third-party streaming
                  software like{' '}
                  <a href="https://obsproject.com/" target="_blank" rel="noreferrer noopener">
                    <b>OBS (Open Broadcaster Software)</b>
                  </a>
                  . There are many third-party software for live streaming, but this guide is
                  specifically for streaming on <b>OBS</b>.
                </div>
                <SizedBox height={16} />
                <CalloutBlock>
                  <div>💡</div>
                  <div>
                    This guide only outlines the basics of using OBS and starting a very simple
                    streaming session. For more information on its advanced usage, visit{' '}
                    <a href="https://obsproject.com/help" target="_blank" rel="noreferrer noopener">
                      https://obsproject.com/help
                    </a>
                    .
                  </div>
                </CalloutBlock>
                <SizedBox height={16} />
                <div>
                  <ReactPlayer
                    url={videos.streamingTutorial}
                    controls={true}
                    width="100%"
                    height="100%"
                  />
                </div>
              </>
            )}
          </div>
        </RhFormGridColumn>
        <RhFormGridColumn>
          <RhCard>
            <H2>RTMP Server URL</H2>
            <SizedBox height={12} />
            <StreamCodeBody>
              <CodeBlock>
                <code>{props.rtmpServerUrl}</code>
              </CodeBlock>
              <RhRawButton
                title="Copy to clipboard"
                onClick={() => {
                  if (props.rtmpServerUrl) {
                    navigator.clipboard.writeText(props.rtmpServerUrl);
                  }
                }}>
                <HiOutlineClipboardCopy size={20} color="#444" />
              </RhRawButton>
            </StreamCodeBody>
          </RhCard>
          <RhCard>
            <H2>Stream Key</H2>
            <SizedBox height={12} />
            {props.isGettingMuxId || props.isLoadingEvent ? (
              <CircleSpinner key="spinner" size={15} color={colors.primary} />
            ) : (
              <StreamCodeBody>
                <CodeBlock>
                  <code>{props.event?.muxID || 'Start event to get a stream key'}</code>
                </CodeBlock>
                <RhRawButton
                  title="Copy to clipboard"
                  disabled={!props.event?.muxID}
                  onClick={() => {
                    if (props.event?.muxID) {
                      navigator.clipboard.writeText(props.event.muxID);
                    }
                  }}>
                  <HiOutlineClipboardCopy size={20} color="#444" />
                </RhRawButton>
              </StreamCodeBody>
            )}
          </RhCard>
        </RhFormGridColumn>
      </StreamViewGrid>
    </DashboardWrapper>
  );
};

const CalloutBlock = styled.div`
  display: flex;
  & > :not(:last-child) {
    margin-right: 16px;
  }
  padding: 16px;
  background-color: beige;
  max-width: 768px;
`;

const CodeBlock = styled.div`
  background-color: #f9fbff;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
`;

const StreamCodeBody = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 8px;
  }
`;

const PlayerDiv = styled.div`
  position: relative;
`;

const PlayerFloater = styled.div`
  position: absolute;
  right: 16px;
  top: 16px;
  border-radius: 4px;
  opacity: 0.75;
`;

const StreamViewGrid = styled.div`
  display: grid;
  grid-template-columns: 5fr 2fr;
  grid-gap: 36px;
`;
